import React, { useEffect, useContext, useState, useCallback } from 'react';
import cn from 'classnames';
import Icon from 'chameleon/packages/blue-book-player/library/icon';
import ButtonWithIcon from 'chameleon/packages/blue-book-player/library/button-with-icon';
import {
  BluebookAnnotationsContext,
  TYPES,
} from 'chameleon/packages/blue-book-player/state/BluebookAnnotationsContext';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import useHighlights from 'chameleon/packages/blue-book-player/Annotation/hooks/useHighlights';
import { SELECTORS } from 'chameleon/packages/blue-book-player/constants/selectors';
import { HIGHLIGHT_COLORS_NAMES } from 'chameleon/packages/blue-book-player/Annotation/constants';
import HighlightColors from 'chameleon/packages/blue-book-player/Annotation/HighlightColors';
import {
  getClickedElementColor,
  computeLearnosityHighlightInfo,
  computeHighlightData,
  getAnnotationId,
  isHighlightActive,
  canAnnotate,
} from 'chameleon/packages/blue-book-player/Annotation/utils';
import { HighlightsNotesHiddenSubjects } from 'chameleon/packages/blue-book-player/constants/subjects';

const COLORS_CLASSES = 'rounded-full h-14 w-14 border border-bluebook-black1';
const HIGHILIGHT_COLORS_OPTIONS = [
  {
    color: HIGHLIGHT_COLORS_NAMES.YELLOW,
    fillColor: '!fill-bluebook-yellow-highlight',
    hoverColor: 'hover:!fill-bluebook-yellow-highlight-selected',
  },
  {
    color: HIGHLIGHT_COLORS_NAMES.BLUE,
    fillColor: '!fill-bluebook-blue-highlight',
    hoverColor: 'hover:!fill-bluebook-blue-highlight-selected',
  },
  {
    color: HIGHLIGHT_COLORS_NAMES.RED,
    fillColor: '!fill-bluebook-red-highlight',
    hoverColor: 'hover:!fill-bluebook-red-highlight-selected',
  },
];

const AnnotationPopover = (props) => {
  const { applyHighlight, removeHighlight } = useHighlights();
  const [highlightData, setHighlightData] = useState({});
  const [learnosityHighlightInfo, setLearnosityHighlightInfo] = useState({});
  const [clickedElementColor, setClickedElementColor] = useState(null);
  const playerEl = useGetPlayerEl();
  const {
    state: {
      isAnnotationEnabled,
      assignment: { id: assignmentId },
    },
  } = useContext(BluebookContext);
  const {
    state: { annotations = [], isNotesColumnExpanded },
    annotationManager: { addAnnotation, setActiveId },
    dispatch,
  } = useContext(BluebookAnnotationsContext);
  const {
    annotationsApp,
    learnosityApp,
    learnosityItemReference,
    isStudentAccount,
    subject,
  } = props;
  const highlightModule = annotationsApp?.module('texthighlight');

  useEffect(() => {
    if (!playerEl) return;
    if (!annotationsApp) return;
    const onHighlight = (highlightInfo) => {
      const { reference } = learnosityApp?.getCurrentItem() || {};
      const endElement = highlightModule.utils.deserializePath(
        `.learnosity-item[data-reference="${reference}"] ${highlightInfo.endContainerPath}`,
        highlightInfo.endOffset,
      );
      const highlightedElement = endElement?.node?.parentElement;
      setTimeout(() => {
        highlightedElement?.click();
      }, 100);
    };

    annotationsApp.on('texthighlight:add', onHighlight);
    return () => {
      annotationsApp.off('texthighlight:add', onHighlight);
    };
  }, [annotationsApp, playerEl, learnosityApp]);

  const handleMouseUp = () => {
    const selection = window.getSelection();
    playerEl.classList.toggle(SELECTORS.CAN_ANNOTATE, canAnnotate(selection));
    if (!isAnnotationEnabled) return;
    if (!selection || !selection.toString()) return;
    setTimeout(() => {
      applyHighlight(selection);
    }, 0);
  };

  const handleTouchEnd = () => {
    const selection = window.getSelection();

    playerEl.classList.toggle(SELECTORS.CAN_ANNOTATE, canAnnotate(selection));
    if (!isAnnotationEnabled) return;
    if (!selection || !selection.toString()) return;
    setTimeout(() => {
      applyHighlight(selection);
    }, 0);
  };

  useEffect(() => {
    if (HighlightsNotesHiddenSubjects.includes(Number(subject.id))) {
      return;
    }
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isAnnotationEnabled, subject.id]);

  const handleClick = (e) => {
    //Show annotation menu when clicking a highlighted text
    const { target } = e;
    const isXMarkerElement =
      target?.classList?.contains(SELECTORS.XMARKER_ELEMENT) ||
      target?.closest(`.${SELECTORS.XMARKER_ELEMENT}`);
    playerEl?.classList?.toggle(
      SELECTORS.SELECTED_ANNOTATION,
      isXMarkerElement,
    );
    setActiveId(null);
    if (isXMarkerElement) {
      const highlightInfo = computeLearnosityHighlightInfo(
        target,
        highlightModule,
        playerEl,
      );
      setClickedElementColor(getClickedElementColor(target));
      setLearnosityHighlightInfo(highlightInfo);
      setHighlightData(
        computeHighlightData(target, highlightInfo, learnosityItemReference),
      );
    }
  };

  useEffect(() => {
    if (highlightData) {
      const annotationId = getAnnotationId(annotations, highlightData);
      if (annotationId) setActiveId(annotationId);
    }
  }, [highlightData]);

  const handleScroll = () => {
    playerEl?.classList?.remove(
      SELECTORS.SELECTED_ANNOTATION,
      SELECTORS.CAN_ANNOTATE,
    );
  };

  useEffect(() => {
    if (!playerEl) return;
    document.body.addEventListener('click', handleClick, true);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.body.removeEventListener('click', handleClick, true);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [playerEl, highlightModule, learnosityItemReference]);

  const onCreateNote = async () => {
    if (!(isHighlightActive() || isAnnotationEnabled)) return;
    if (isStudentAccount) {
      const createAnnotationData = {
        annotation: '',
        assignmentId,
        highlight: highlightData,
      };
      await addAnnotation(createAnnotationData);
      if (!isNotesColumnExpanded) dispatch({ type: TYPES.TOGGLE_NOTES_COLUMN });
    }
  };

  const selectedColorOption =
    HIGHILIGHT_COLORS_OPTIONS.find(
      (option) => option.color === clickedElementColor,
    ) || HIGHILIGHT_COLORS_OPTIONS[0];

  const onEscapeKey = (e) => {
    if (e.key === 'Escape') {
      e.target
        .querySelector(SELECTORS.ANNOTATION_POPOVER_CONTAINER)
        .classList.add('lrn-annotations-c-popover--hidden');
    }
  };
  useEffect(() => {
    document.addEventListener('keydown', onEscapeKey);
    return () => {
      document.removeEventListener('keydown', onEscapeKey);
    };
  }, []);

  const onDeleteHighlight = useCallback(() => {
    document
      .querySelector(SELECTORS.ANNOTATION_POPOVER_CONTAINER)
      .classList.add('lrn-annotations-c-popover--hidden');
    removeHighlight();
  });

  return (
    <div className="flex bg-white p-3 shadow-[0_5px_13px_-4px_rgba(0,0,0,0.5)] justify-center items-center rounded-full gap-4 absolute top-0">
      <HighlightColors
        highlightModule={highlightModule}
        learnosityHighlightInfo={learnosityHighlightInfo}
        clickedHighlightColor={clickedElementColor}
        isAnnotationEnabled={isAnnotationEnabled}
      />
      <div className="flex gap-1 justify-center items-center font-semibold h-14 w-14 text-center">
        <span className="border-dashed border-bluebook-black1 border-b pb-[1px] px-[3px]">
          U
        </span>
        <Icon
          id="question_navigator_arrow"
          width={12}
          height={12}
          className="rotate-180"
        />
      </div>
      <ButtonWithIcon
        icon="highlight_delete"
        isIcon
        className={cn(
          COLORS_CLASSES,
          'flex justify-center items-center bg-white hover:bg-bluebook-light-gray hover:border-2',
        )}
        iconClassName="hover:!fill-white !fill-bluebook-light-gray"
        onClick={onDeleteHighlight}
        onTouchEnd={onDeleteHighlight}
      />
      <div className="border-l border-bluebook-gray5 h-8" />
      <ButtonWithIcon
        icon="highlight_note"
        isIcon
        className={cn(
          COLORS_CLASSES,
          `flex justify-center items-center bg-white hover:border-2`,
        )}
        iconClassName={`${selectedColorOption.fillColor} ${selectedColorOption.hoverColor}`}
        onClick={onCreateNote}
        onTouchEnd={onCreateNote}
      />
    </div>
  );
};

export default AnnotationPopover;
